/**
 * GA4 analytics helpers. Requires gtag (from GoogleAnaytics / index.html).
 *
 * In GA4, find these events under: Reports > Engagement > Events.
 * Event names are exactly as sent (e.g. subscription_upgraded, subscription_activated).
 */
const GA4_MEASUREMENT_ID = "G-VC7DZTPE7E";

function getGtag() {
  return typeof window !== "undefined" && typeof window.gtag === "function" ? window.gtag : null;
}

/**
 * AUD value of one conversion, per tier. Used as the GA4 `purchase` value so
 * Google Ads can bid on revenue rather than on raw clicks. Yearly is the full
 * A$500; monthly tiers are counted at their first payment.
 */
const TIER_VALUE_AUD = { basic: 25, premium: 50, premiumYearly: 500, premium_yearly: 500 };
export function tierValueAud(tier) {
  return TIER_VALUE_AUD[tier] != null ? TIER_VALUE_AUD[tier] : 50;
}

/**
 * Funnel step 1 — a new account was created (not yet paying).
 * `sign_up` is a GA4 recommended event name, so it shows in the standard reports.
 * @param {Object} params - e.g. { method: 'password'|'google' }
 */
export function sendSignUp(params = {}) {
  const gtag = getGtag();
  if (gtag) gtag("event", "sign_up", { ...params, send_to: GA4_MEASUREMENT_ID });
}

/**
 * Funnel step 2 — the user was sent to Stripe Checkout. Fires immediately before
 * the redirect, so it is the last thing we can observe on our own domain.
 * @param {Object} params - e.g. { tier, value, promo_code }
 */
export function sendBeginCheckout(params = {}) {
  const gtag = getGtag();
  if (gtag) {
    gtag("event", "begin_checkout", {
      currency: "AUD",
      ...params,
      send_to: GA4_MEASUREMENT_ID,
    });
  }
}

/**
 * Funnel step 3 — checkout verified, the subscription is live.
 *
 * Sends the standard GA4 ecommerce `purchase` event. `purchase` has been a GA4
 * key event on this property since 2026-03-04 but nothing ever emitted it, so
 * Google Ads was optimising toward a conversion that could never happen. This
 * is what makes that key event real. `subscription_activated` is still sent
 * separately by the caller for the internal reporting that already uses it.
 *
 * @param {Object} p - { tier, transactionId, value? }
 */
export function sendPurchase({ tier, transactionId, value } = {}) {
  const gtag = getGtag();
  if (!gtag) return;
  const amount = value != null ? value : tierValueAud(tier);
  gtag("event", "purchase", {
    // transaction_id dedupes if the user reloads the post-checkout URL.
    transaction_id: transactionId || `sub-${tier || "premium"}-${Date.now()}`,
    value: amount,
    currency: "AUD",
    items: [{ item_id: tier || "premium", item_name: `Membership: ${tier || "premium"}`, price: amount, quantity: 1 }],
    send_to: GA4_MEASUREMENT_ID,
  });
}

/**
 * Send a subscription-related event to GA4.
 * Events: "subscription_activated", "subscription_upgraded" (use underscore, not space).
 * @param {string} eventName - e.g. "subscription_activated", "subscription_upgraded"
 * @param {Object} params - e.g. { from_tier, to_tier, upgrade_source }
 */
export function sendSubscriptionEvent(eventName, params = {}) {
  const gtag = getGtag();
  if (gtag) gtag("event", eventName, { ...params, send_to: GA4_MEASUREMENT_ID });
}

/**
 * Send a practice/trainer event to GA4.
 * @param {string} eventName - e.g. "practice_view", "practice_problem_complete"
 * @param {Object} params - e.g. { trainer, category_key, puzzle_id, difficulty }
 */
export function sendPracticeEvent(eventName, params = {}) {
  const gtag = getGtag();
  if (gtag) gtag("event", eventName, { ...params, send_to: GA4_MEASUREMENT_ID });
}

/**
 * UTM / click IDs from the landing URL for attribution on CTA events (GA4).
 * @param {string} search - location.search, e.g. "?utm_source=google&gclid=..."
 */
export function getMarketingParamsFromSearch(search) {
  const params = new URLSearchParams(search || "");
  const keys = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "gclid",
    "gbraid",
    "wbraid",
  ];
  const out = {};
  keys.forEach((k) => {
    const v = params.get(k);
    if (v) out[k] = v;
  });
  return out;
}

/**
 * Primary CTA on /beginner landing (Google Ads funnel).
 * @param {Object} params - e.g. { placement: 'hero'|'bottom'|'sticky', destination, ...utm_* , gclid }
 */
export function sendBeginnerLandingCtaClick(params = {}) {
  const gtag = getGtag();
  if (gtag) gtag("event", "beginner_landing_cta_click", { ...params, send_to: GA4_MEASUREMENT_ID });
}

/**
 * GA4 — Treadmill drills (/treadmill, /treadmill/practice).
 * Reports > Engagement > Events (mark as key events if you want conversions).
 *
 * Events:
 * - treadmill_landing_view — user opened /treadmill marketing page
 * - treadmill_tool_select — practice page; params include treadmill_tool + auth_segment
 *
 * Recommended GA4 custom dimensions (Admin > Custom definitions): register event parameters
 * `treadmill_tool`, `auth_segment` if you want breakdown charts.
 *
 * @param {string} eventName
 * @param {Object} params - treadmill_tool?: 'hand_shape'|'opponent_shape'|'building_blocks', auth_segment?: 'guest'|'free_signed_in'|'subscriber'
 */
export function sendTreadmillEvent(eventName, params = {}) {
  const gtag = getGtag();
  if (gtag) gtag("event", eventName, { ...params, send_to: GA4_MEASUREMENT_ID });
}

/**
 * GA4 — Just Play table (/just-play) + Weekly Tournament (/weekly).
 *
 * Page views (and therefore per-page user/session counts AND engagement time, broken
 * down by the subscription_active / tier user-properties) are already tracked
 * automatically by GoogleAnaytics.js on every route change — so "how many use it" and
 * "for how long" need nothing here. These events add action-level depth:
 * - just_play_hand_complete   — a deal was played to the end in Just Play
 * - weekly_board_complete     — params: { board_no } — a tournament board was finished
 * - weekly_tournament_finished — all 10 tournament boards completed
 *
 * In GA4: Reports > Engagement > Events. Register `board_no` as a custom dimension if
 * you want per-board breakdowns.
 */
export function sendPlayEvent(eventName, params = {}) {
  const gtag = getGtag();
  if (gtag) gtag("event", eventName, { ...params, send_to: GA4_MEASUREMENT_ID });
}

/** For GA4 param `auth_segment` on Treadmill events (compact segments). */
export function treadmillAuthSegmentForGa({ uid, subscriptionActive, isAdmin }) {
  if (!uid) return "guest";
  if (isAdmin || subscriptionActive === true) return "subscriber";
  return "free_signed_in";
}
